import type {TicketFiltersValue} from 'common/ticket-filters/types';
import type {TicketingListResponse} from 'common/ticketing-list/types';

import {FilterOutlined} from '@ant-design/icons';
import {Loading} from '@ds/components';
import {PageLayout} from '@ds/layout';
import {useRouteBuildContext} from '@router';
import Empty from 'common/empty/Empty';
import TicketFilters from 'common/ticket-filters/TicketFilters';
import {createTicketFiltersFromQuery} from 'common/ticket-filters/utils';
import TicketingListItem from 'common/ticketing-list/TicketingListItem';
import {useCallback, useEffect, useMemo, useState} from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import {useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'store';
import {emptyTrackings} from 'store/slices/tracking/slicer';
import {getTrackings} from 'store/slices/tracking/thunk';
import {StringParam, useQueryParams, withDefault} from 'use-query-params';
import {getSelectedVendorFromStorage} from 'utils/Helper';

const TRACKING_PAGE_SIZE = 20;

const Tracking: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {updateHeaderData} = useRouteBuildContext();
  const selectedVendor = getSelectedVendorFromStorage();
  const vendorId =
    typeof selectedVendor?.id === 'string' || typeof selectedVendor?.id === 'number'
      ? selectedVendor.id
      : '0';
  const {trackings, loading} = useAppSelector((state) => state.tracking);
  const [query, setQuery] = useQueryParams({
    order_code: withDefault(StringParam, null),
    type: withDefault(StringParam, null),
    state: withDefault(StringParam, null)
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [page, setPage] = useState(1);

  const filters = useMemo<TicketFiltersValue>(() => createTicketFiltersFromQuery(query), [query]);

  const currentTrackings = useMemo<TicketingListResponse>(() => {
    return {
      tickets: Array.isArray(trackings?.tickets) ? trackings.tickets : [],
      total_items: typeof trackings?.total_items === 'number' ? trackings.total_items : 0
    };
  }, [trackings]);

  const loadTrackings = useCallback(
    (requestedPage = 1, selectedFilters = filters) => {
      void dispatch(
        getTrackings({
          vendor_id: vendorId,
          initiator: 'snapp',
          page_size: TRACKING_PAGE_SIZE,
          page: requestedPage,
          ...(selectedFilters.order_code.trim()
            ? {order_code: selectedFilters.order_code.trim()}
            : {}),
          ...(selectedFilters.types.length > 0 ? {type: selectedFilters.types.join(',')} : {}),
          ...(selectedFilters.states.length > 0 ? {state: selectedFilters.states} : {})
        })
      );
    },
    [dispatch, filters, vendorId]
  );

  useEffect(() => {
    loadTrackings();
  }, [loadTrackings]);

  const activeFilterCount =
    Number(Boolean(filters.order_code.trim())) +
    Number(filters.types.length > 0) +
    Number(filters.states.length > 0);

  useEffect(() => {
    updateHeaderData('tracking', {
      icon: <FilterOutlined aria-hidden />,
      action: () => setIsFilterOpen(true),
      badgeNumber: activeFilterCount,
      iconButtonSize: 'sm'
    });
  }, [activeFilterCount, updateHeaderData]);

  const loadMoreTrackings = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    loadTrackings(nextPage);
  };

  const isInitialLoading = Boolean(loading) && currentTrackings.tickets.length === 0;
  const hasMoreTrackings = currentTrackings.tickets.length < currentTrackings.total_items;

  const handleApplyFilters = (nextFilters: TicketFiltersValue) => {
    dispatch(emptyTrackings());
    setPage(1);
    setIsFilterOpen(false);
    void setQuery(
      {
        order_code: nextFilters.order_code.trim() || null,
        type: nextFilters.types.length > 0 ? nextFilters.types.join(',') : null,
        state: nextFilters.states.length > 0 ? nextFilters.states.join(',') : null
      },
      'replace'
    );
  };

  return (
    <PageLayout contentId="tracking-list">
      {isInitialLoading ? (
        <div className="flex min-h-60 flex-1 items-center justify-center">
          <Loading size="large" color="primary" />
        </div>
      ) : currentTrackings.tickets.length === 0 ? (
        <div className="flex min-h-60 flex-1 items-center justify-center px-10 text-center">
          <Empty>
            <p className="text-body-md-emphasis text-neutral-fg">اطلاعاتی برای نمایش وجود ندارد</p>
          </Empty>
        </div>
      ) : (
        <InfiniteScroll
          scrollableTarget="tracking-list"
          scrollThreshold={0.6}
          next={loadMoreTrackings}
          hasMore={hasMoreTrackings}
          dataLength={currentTrackings.tickets.length}
          loader={
            <div className="flex h-10 justify-center">
              <Loading size="medium" color="primary" />
            </div>
          }
        >
          {currentTrackings.tickets.map((tracking) => (
            <TicketingListItem
              key={tracking.id}
              item={tracking}
              onClick={() =>
                navigate(`/tracking/${tracking.id}`, {
                  state: {orderCode: tracking.order_code ?? null}
                })
              }
            />
          ))}
        </InfiniteScroll>
      )}
      <TicketFilters
        show={isFilterOpen}
        filters={filters}
        onApply={handleApplyFilters}
        onClose={() => setIsFilterOpen(false)}
      />
    </PageLayout>
  );
};

export default Tracking;
